import { Drawer as AntDrawer, Input } from 'antd'
import { observer } from 'mobx-react-lite'
import { type ChangeEvent, type ReactNode, useEffect, useRef } from 'react'
import { MdOutlineAddPhotoAlternate, MdOutlinePhotoCamera } from 'react-icons/md'

import { formatDuration, formatKm } from '@/Containers/ShiftSection/ShiftContainer/Helpers/shiftFormat'
import { formatAppDate } from '@/Ship/Helpers/appTime'
import { cx } from '@/Ship/Helpers/cssClass'
import { HEIC_ACCEPT, preparePhoto } from '@/Ship/Helpers/heicPhoto'
import { getPhotoValidationError } from '@/Ship/Helpers/photoRules'

import { MAX_SHIFT_PHOTOS, type MyShiftStore, formatShiftDay } from '../../Stores/MyShiftStore'

import styles from './CloseShiftSheet.module.css'

const formatMoment = (value: string | null | undefined): string => formatAppDate(value, 'DD.MM.YYYY, HH:mm') ?? '—'

interface CloseShiftSheetProps {
    store: MyShiftStore
    isOffline: boolean
    /** Завдання активної зміни в роботі: поки вони є, зміну не закрити */
    inProgressCount: number
    /** Офлайн-дії ще не дійшли до сервера — він не бачить завдань у роботі */
    syncPending: boolean
}

/** Рядок підсумку: назва зліва, значення справа */
const Row = ({
    label,
    children,
    variant,
}: {
    label: string
    children: ReactNode
    /** strong — головне значення; stack — довгий текст під підписом */
    variant?: 'strong' | 'stack'
}) => (
    <div className={[cx(styles, 'row'), variant ? cx(styles, `row--${variant}`) : ''].join(' ')}>
        <dt>{label}</dt>
        <dd>{children}</dd>
    </div>
)

/**
 * Закриття зміни водієм — одна шторка, три кроки: попередження (або чому закрити ще не можна),
 * дані закриття (кінцевий одометр, зауваження, фото несправностей), перевірка підсумку.
 * Зміна закривається лише кнопкою «Закрити зміну» на кроці перевірки.
 */
export const CloseShiftSheet = observer(({ store, isOffline, inProgressCount, syncPending }: CloseShiftSheetProps) => {
    const step = store.closeStep
    const shift = store.active
    const galleryRef = useRef<HTMLInputElement>(null)
    const cameraRef = useRef<HTMLInputElement>(null)
    const isTouchDevice = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0

    // «Поточний час завершення» лишається поточним, поки водій читає підсумок
    useEffect(() => {
        if (step !== 'review') return undefined
        const timer = window.setInterval(() => store.tickReview(), 30_000)
        return () => window.clearInterval(timer)
    }, [step, store])

    const addPhotos = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? [])
        // Скидаємо одразу: те саме фото можна обрати ще раз
        event.target.value = ''
        if (files.length === 0) return

        const session = store.closeSession
        store.setPhotosProcessing(true)
        const errors: string[] = []
        try {
            for (const file of files) {
                if (store.photos.length >= MAX_SHIFT_PHOTOS) {
                    errors.push(`Можна додати не більше ${MAX_SHIFT_PHOTOS} фото`)
                    break
                }
                let compressed: File
                try {
                    // HEIC з iPhone → JPEG, далі стиснення під ліміт
                    compressed = await preparePhoto(file)
                } catch (err) {
                    errors.push(err instanceof Error ? err.message : `«${file.name}»: не вдалося обробити фото`)
                    continue
                }
                const invalid = getPhotoValidationError(compressed)
                if (invalid) {
                    errors.push(invalid)
                    continue
                }
                // Шторку закрили під час обробки — фото вже нікому не потрібні
                if (!store.addPhoto(compressed, URL.createObjectURL(compressed), session)) break
            }
        } finally {
            store.setPhotosProcessing(false)
            if (session === store.closeSession) store.setFormError(errors.length > 0 ? errors.join('\n') : null)
        }
    }

    const blocked = inProgressCount > 0 || syncPending
    const eyebrow = shift ? `Зміна ${formatShiftDay(shift.shiftDate)} · ${shift.code}` : 'Зміна'
    const summary = store.summary

    let content: ReactNode = null

    if (step === 'warn' && blocked) {
        content = (
            <>
                <h2 id="close-shift-title" className={cx(styles, 'title')}>
                    {inProgressCount > 0 ? 'Спершу завершіть завдання в роботі' : 'Зачекайте на синхронізацію'}
                </h2>
                <p className={cx(styles, 'lead')} data-testid="shift-tasks-in-progress">
                    {inProgressCount > 0
                        ? `Зараз виконуються завдання: ${inProgressCount}. Зміну можна закрити, коли всі вони завершені.`
                        : "Дії, виконані без зв'язку, ще надсилаються. Закрийте зміну, коли вони дійдуть до сервера."}
                </p>
                <div className={cx(styles, 'buttons')}>
                    <button
                        type="button"
                        className="pwa-btn pwa-btn--secondary pwa-btn--block"
                        onClick={() => store.close()}
                    >
                        Зрозуміло
                    </button>
                </div>
            </>
        )
    } else if (step === 'warn') {
        content = (
            <>
                <h2 id="close-shift-title" className={cx(styles, 'title')}>
                    Ви дійсно бажаєте закрити робочу зміну?
                </h2>
                <p className={cx(styles, 'lead')}>Після закриття зміни облік вашого робочого часу буде зупинено.</p>
                <div className={cx(styles, 'buttons')}>
                    <button
                        type="button"
                        className="pwa-btn pwa-btn--primary pwa-btn--block"
                        data-testid="close-shift-continue"
                        onClick={() => store.continueClosing()}
                    >
                        Продовжити закриття
                    </button>
                    <button
                        type="button"
                        className="pwa-btn pwa-btn--secondary pwa-btn--block"
                        onClick={() => store.close()}
                    >
                        Скасувати
                    </button>
                </div>
            </>
        )
    } else if (step === 'form') {
        content = (
            <>
                <h2 id="close-shift-title" className={cx(styles, 'title')}>
                    Дані закриття зміни
                </h2>

                <label className={cx(styles, 'field')}>
                    <span className={cx(styles, 'fieldLabel')}>
                        <span>
                            Кінцевий одометр, км <span className={cx(styles, 'required')}>*</span>
                        </span>
                    </span>
                    <Input
                        size="large"
                        inputMode="numeric"
                        placeholder={
                            typeof shift?.startOdometer === 'number'
                                ? `Не менше ${shift.startOdometer}`
                                : 'Напр. 125700'
                        }
                        value={store.endOdometer}
                        {...(store.endOdometerError ? { status: 'error' as const } : {})}
                        onChange={(e) => store.setEndOdometer(e.target.value)}
                        data-testid="shift-end-odometer"
                    />
                    {store.endOdometerError && (
                        <span className={cx(styles, 'error')} role="alert" data-testid="shift-end-odometer-error">
                            {store.endOdometerError}
                        </span>
                    )}
                    {/* Початковий показник — і під помилкою: водію є з чим звірити введене */}
                    {typeof shift?.startOdometer === 'number' && (
                        <span className={cx(styles, 'note')}>Початковий — {formatKm(shift.startOdometer)}</span>
                    )}
                </label>

                <label className={cx(styles, 'field')}>
                    <span className={cx(styles, 'fieldLabel')}>Зауваження до технічного стану ТЗ</span>
                    <Input.TextArea
                        size="large"
                        rows={3}
                        maxLength={5000}
                        placeholder="Необов'язково, якщо зауважень немає"
                        value={store.technicalRemarks}
                        onChange={(e) => store.setTechnicalRemarks(e.target.value)}
                        data-testid="shift-technical-remarks"
                    />
                </label>

                <div className={cx(styles, 'field')}>
                    <span className={cx(styles, 'fieldLabel')}>
                        Фото несправностей або пошкоджень
                        <span className={cx(styles, 'counter')}>
                            {store.photos.length} з {MAX_SHIFT_PHOTOS}
                        </span>
                    </span>
                    {store.photos.length > 0 && (
                        <div className={cx(styles, 'photos')}>
                            {store.photos.map((photo) => (
                                <div key={photo.id} className={cx(styles, 'photo')}>
                                    <img src={photo.preview} alt="" />
                                    <button
                                        type="button"
                                        className={cx(styles, 'photoRemove')}
                                        onClick={() => store.removePhoto(photo.id)}
                                        aria-label="Прибрати фото"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <input
                        ref={galleryRef}
                        type="file"
                        accept={`image/*,${HEIC_ACCEPT}`}
                        multiple
                        hidden
                        onChange={addPhotos}
                        data-testid="shift-photo-input"
                    />
                    {isTouchDevice && (
                        <input
                            ref={cameraRef}
                            type="file"
                            accept={`image/*,${HEIC_ACCEPT}`}
                            capture="environment"
                            hidden
                            onChange={addPhotos}
                        />
                    )}
                    <div className={cx(styles, 'photoButtons')}>
                        <button
                            type="button"
                            className="pwa-btn pwa-btn--secondary pwa-btn--compact"
                            disabled={!store.canAddPhoto}
                            onClick={() => galleryRef.current?.click()}
                        >
                            <MdOutlineAddPhotoAlternate /> {isTouchDevice ? 'Галерея' : 'Додати фото'}
                        </button>
                        {isTouchDevice && (
                            <button
                                type="button"
                                className="pwa-btn pwa-btn--secondary pwa-btn--compact"
                                disabled={!store.canAddPhoto}
                                onClick={() => cameraRef.current?.click()}
                            >
                                <MdOutlinePhotoCamera /> Зняти фото
                            </button>
                        )}
                    </div>
                    {store.photosProcessing && <span className={cx(styles, 'note')}>Обробка фото…</span>}
                </div>

                {store.formError && <div className="pwa-note pwa-note--danger">{store.formError}</div>}

                <div className={cx(styles, 'buttons')}>
                    <button
                        type="button"
                        className="pwa-btn pwa-btn--primary pwa-btn--block"
                        disabled={!store.completeValid || store.photosProcessing}
                        onClick={() => store.toReview()}
                        data-testid="close-shift-review"
                    >
                        Далі
                    </button>
                    <button
                        type="button"
                        className="pwa-btn pwa-btn--secondary pwa-btn--block"
                        onClick={() => store.close()}
                    >
                        Скасувати
                    </button>
                </div>
            </>
        )
    } else if (step === 'review' && summary) {
        const remarks = store.technicalRemarks.trim()
        content = (
            <>
                <h2 id="close-shift-title" className={cx(styles, 'title')}>
                    Перевірте дані зміни
                </h2>

                {/* Одометр як у дорожньому листі: показники й пробіг між ними — головне, що перевіряє водій */}
                <div className={cx(styles, 'odometer')}>
                    <div className={cx(styles, 'reading')}>
                        <span className={cx(styles, 'readingLabel')}>Початковий</span>
                        <span className={cx(styles, 'readingValue')}>{formatKm(summary.startOdometer)}</span>
                    </div>
                    <div className={cx(styles, 'mileage')} data-testid="shift-mileage">
                        <span className={cx(styles, 'readingLabel')}>Пробіг за зміну</span>
                        <span className={cx(styles, 'mileageValue')}>{formatKm(summary.mileage)}</span>
                    </div>
                    <div className={cx(styles, 'reading')}>
                        <span className={cx(styles, 'readingLabel')}>Кінцевий</span>
                        <span className={cx(styles, 'readingValue')}>{formatKm(summary.endOdometer)}</span>
                    </div>
                </div>

                <dl className={cx(styles, 'summary')}>
                    <Row label="Транспортний засіб">{summary.vehicle ?? '—'}</Row>
                    <Row label="Початок зміни">{formatMoment(summary.startedAt)}</Row>
                    <Row label="Завершення">{formatMoment(summary.endedAt)}</Row>
                    <Row label="Тривалість зміни" variant="strong">
                        {formatDuration(summary.durationMinutes)}
                    </Row>
                    {remarks && (
                        <Row label="Зауваження до технічного стану ТЗ" variant="stack">
                            {remarks}
                        </Row>
                    )}
                    {store.photos.length > 0 && <Row label="Фото несправностей">{store.photos.length}</Row>}
                </dl>

                {store.formError && <div className="pwa-note pwa-note--danger">{store.formError}</div>}

                <div className={cx(styles, 'buttons')}>
                    <button
                        type="button"
                        className="pwa-btn pwa-btn--primary pwa-btn--block"
                        disabled={store.submitting}
                        onClick={() => store.complete(isOffline)}
                        data-testid="close-shift-confirm"
                    >
                        {store.submitting ? 'Закриваємо…' : 'Закрити зміну'}
                    </button>
                    <button
                        type="button"
                        className="pwa-btn pwa-btn--secondary pwa-btn--block"
                        disabled={store.submitting}
                        onClick={() => store.backToForm()}
                    >
                        Змінити дані
                    </button>
                </div>
            </>
        )
    }

    const stepNumber = step === 'form' ? 2 : step === 'review' ? 3 : 1

    return (
        <AntDrawer
            open={step !== null}
            onClose={() => store.close()}
            placement="bottom"
            height="auto"
            closable={false}
            maskClosable={!store.submitting}
            keyboard={!store.submitting}
            title={null}
            destroyOnHidden
            styles={{
                wrapper: { background: 'transparent', boxShadow: 'none' },
                section: { background: 'var(--pwa-surface)', borderRadius: '18px 18px 0 0', maxHeight: '92dvh' },
                body: { padding: 0, overflowY: 'auto' },
            }}
        >
            {content && (
                <div
                    className={cx(styles, 'sheet')}
                    role={step === 'warn' ? 'alertdialog' : 'dialog'}
                    aria-labelledby="close-shift-title"
                    data-testid="close-shift-sheet"
                >
                    <span className={cx(styles, 'grab')} aria-hidden />
                    <div className={cx(styles, 'eyebrow')}>
                        <span className="pwa-label">{eyebrow}</span>
                        {!(step === 'warn' && blocked) && (
                            <span className={cx(styles, 'steps')} aria-label={`Крок ${stepNumber} з 3`}>
                                {[1, 2, 3].map((n) => (
                                    <span
                                        key={n}
                                        className={[
                                            cx(styles, 'dot'),
                                            n <= stepNumber ? cx(styles, 'dot--on') : '',
                                        ].join(' ')}
                                    />
                                ))}
                            </span>
                        )}
                    </div>
                    {content}
                </div>
            )}
        </AntDrawer>
    )
})
